import { useI18n } from 'vue-i18n'
import type { ChartInterval } from 'shared'

type Translate = (key: string) => string

interface FormatOptions {
  t: Translate
  formatAbsoluteLabel: (date: Date, interval: ChartInterval | '15m') => string
  useCurrentLabels?: boolean
  now?: Date
}

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

const isSameHour = (a: Date, b: Date) => isSameDay(a, b) && a.getHours() === b.getHours()

const isSameMonth = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()

const isSameQuarter = (a: Date, b: Date) =>
  isSameYear(a, b) && Math.floor(a.getMonth() / 3) === Math.floor(b.getMonth() / 3)

const isSameYear = (a: Date, b: Date) => a.getFullYear() === b.getFullYear()

const getISOWeekInfo = (date: Date) => {
  const temp = new Date(date.getTime())
  temp.setHours(0, 0, 0, 0)
  temp.setDate(temp.getDate() + 3 - ((temp.getDay() + 6) % 7))
  const weekYear = temp.getFullYear()
  const week1 = new Date(weekYear, 0, 4)
  const week =
    1 +
    Math.round(((temp.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
  return { week, year: weekYear }
}

export const getCurrentHighlightIndex = (
  labels: string[],
  interval: ChartInterval,
): number | undefined => {
  if (!labels.length) return undefined

  const now = new Date()
  const nowWeek = getISOWeekInfo(now)

  const matchers: Record<ChartInterval, (date: Date) => boolean> = {
    hour: (date) => isSameHour(date, now),
    day: (date) => isSameDay(date, now),
    week: (date) => {
      const week = getISOWeekInfo(date)
      return week.week === nowWeek.week && week.year === nowWeek.year
    },
    month: (date) => isSameMonth(date, now),
    quarter: (date) => isSameQuarter(date, now),
    year: (date) => isSameYear(date, now),
  }

  const match = matchers[interval]

  const index = labels.findIndex((label) => {
    const date = new Date(label)
    if (Number.isNaN(date.getTime())) return false
    return match(date)
  })

  return index >= 0 ? index : undefined
}

export const formatChartCategories = (
  labels: string[],
  interval: ChartInterval | '15m',
  { t, formatAbsoluteLabel, useCurrentLabels = true, now = new Date() }: FormatOptions,
): string[] => {
  const nowWeek = getISOWeekInfo(now)

  return labels.map((label) => {
    const date = new Date(label)
    if (Number.isNaN(date.getTime())) return label

    switch (interval) {
      case '15m':
        return formatAbsoluteLabel(date, interval)
      case 'hour':
        return useCurrentLabels && isSameHour(date, now)
          ? t('this_hour')
          : formatAbsoluteLabel(date, interval)
      case 'day':
        return useCurrentLabels && isSameDay(date, now)
          ? t('today')
          : formatAbsoluteLabel(date, interval)
      case 'week': {
        const week = getISOWeekInfo(date)
        return useCurrentLabels && week.week === nowWeek.week && week.year === nowWeek.year
          ? t('this_week')
          : formatAbsoluteLabel(date, interval)
      }
      case 'month':
        return useCurrentLabels && isSameMonth(date, now)
          ? t('this_month')
          : formatAbsoluteLabel(date, interval)
      case 'quarter':
        return useCurrentLabels && isSameQuarter(date, now)
          ? t('this_quarter')
          : formatAbsoluteLabel(date, interval)
      case 'year':
        return useCurrentLabels && isSameYear(date, now)
          ? t('this_year')
          : formatAbsoluteLabel(date, interval)
      default:
        return label
    }
  })
}

/**
 * Translates chart series labels using the i18n `charts.units.*` keys.
 * Leaves non-string or empty labels untouched.
 *
 * @param series - Chart series array (any shape with an optional label field).
 * @param t - Translation function to apply to string labels.
 * @returns New series array with translated labels.
 */
export const localizeChartUnits = <T extends { label?: unknown }>(
  series: readonly T[],
  t: ReturnType<typeof useI18n>['t'],
  i18nPrefix = 'charts.units',
): T[] => {
  return series.map((item) => ({
    ...item,
    label:
      typeof item.label === 'string' && item.label.length > 0
        ? t(`${i18nPrefix}.${item.label}`)
        : item.label,
  }))
}
